import { useEffect, useState } from "react";

interface SplashScreenProps {
  onFinish: () => void;
}

export default function SplashScreen({ onFinish }: SplashScreenProps) {
  const [phase, setPhase] = useState<"enter" | "hold" | "exit">("enter");

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 600);
    const t2 = setTimeout(() => setPhase("exit"), 2200);
    const t3 = setTimeout(() => onFinish(), 2800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onFinish]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 40%, #fecdd3 100%)",
        transition: "opacity 0.6s ease",
        opacity: phase === "exit" ? 0 : 1,
        pointerEvents: phase === "exit" ? "none" : "all",
      }}
    >
      {/* Círculos decorativos de fundo */}
      <div
        style={{
          position: "absolute",
          top: "-80px",
          right: "-80px",
          width: "320px",
          height: "320px",
          borderRadius: "50%",
          background: "rgba(244, 63, 94, 0.08)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-60px",
          left: "-60px",
          width: "260px",
          height: "260px",
          borderRadius: "50%",
          background: "rgba(244, 63, 94, 0.06)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          left: "-40px",
          width: "160px",
          height: "160px",
          borderRadius: "50%",
          background: "rgba(244, 63, 94, 0.05)",
        }}
      />

      {/* Conteúdo central */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
          animation: phase === "enter" ? "splashEnter 0.6s cubic-bezier(0.16,1,0.3,1) both" : undefined,
        }}
      >
        {/* Logo */}
        <div
          style={{
            width: "110px",
            height: "110px",
            borderRadius: "32px",
            background: "white",
            boxShadow: "0 20px 60px rgba(244, 63, 94, 0.25), 0 4px 16px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            animation: "splashPulse 2s ease-in-out infinite",
          }}
        >
          <img
            src="https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png"
            alt="EntreNós"
            style={{ width: "80px", height: "80px", objectFit: "contain" }}
          />
        </div>

        {/* Nome do app */}
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "36px",
              fontWeight: 700,
              color: "#be123c",
              letterSpacing: "-0.5px",
              lineHeight: 1,
              margin: 0,
            }}
          >
            EntreNós
          </h1>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "13px",
              color: "#f43f5e",
              marginTop: "8px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            Conexão que fortalece
          </p>
        </div>

        {/* Corações animados */}
        <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                fontSize: "16px",
                animation: `heartDot 1.4s ease-in-out ${i * 0.2}s infinite`,
                display: "inline-block",
              }}
            >
              ♥
            </span>
          ))}
        </div>
      </div>

      {/* Barra de progresso */}
      <div
        style={{
          position: "absolute",
          bottom: "48px",
          width: "120px",
          height: "3px",
          background: "rgba(244, 63, 94, 0.15)",
          borderRadius: "99px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            background: "linear-gradient(90deg, #f43f5e, #be123c)",
            borderRadius: "99px",
            animation: "splashProgress 2s ease-out forwards",
          }}
        />
      </div>

      <style>{`
        @keyframes splashEnter {
          from { opacity: 0; transform: scale(0.85) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes splashPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 20px 60px rgba(244,63,94,0.25), 0 4px 16px rgba(0,0,0,0.08); }
          50%       { transform: scale(1.04); box-shadow: 0 24px 70px rgba(244,63,94,0.35), 0 4px 20px rgba(0,0,0,0.1); }
        }
        @keyframes heartDot {
          0%, 100% { opacity: 0.3; transform: scale(1); color: #f43f5e; }
          50%       { opacity: 1;   transform: scale(1.4); color: #be123c; }
        }
        @keyframes splashProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </div>
  );
}
